import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {Observable, Subject} from "rxjs";
import {finalize} from "rxjs/operators";
import {loadAudioFromBlob} from "../../defines/wav_process.func";


const constraints = {
  audio: {
    echoCancellation: false, // disabling audio processing
    googAutoGainControl: true,
    googNoiseSuppression: true,
    googHighpassFilter: true,
    googTypingNoiseDetection: true,
    //googAudioMirroring: true
    sampleRate: 16000,
    channelCount: 1
  },
  video: false
};

@Injectable({
  providedIn: 'root'
})
export class RecorderService implements OnDestroy {

  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  public recording$: Subject<ArrayBuffer> | null = null;

  public get isRecording(): boolean {
    return this.recording$ !== null;
  }

  constructor(
    private zone: NgZone,
  ) {
  }

  ngOnDestroy() {
    console.log('RecorderService: ngOnDestroy');

    if (this.mediaRecorder) {
      if (this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;
    }

    if (this.stream && this.stream.active) {
      this.stream.getTracks().forEach(track => {
        track.removeEventListener('ended', this.onTrackEnded);
        track.stop();
      });
    }
    this.stream = null;

    if (this.recording$) {
      this.recording$.complete();
      this.recording$ = null;
    }
  }

  private onTrackEnded = (event: Event) => {
    console.warn('Track ended:', event);
    // 필요에 따라 스트림을 다시 초기화하거나 사용자에게 알립니다.
    this.stream = null;
  };

  public async init() {
    try {
      this.stream = await this.getMediaStream(constraints);
      console.log('init() stream =', this.stream);
    } catch (err: any) {
      if (err.name === 'PermissionDeniedError' || err.name === 'NotAllowedError') {
        console.error('User denied permission.', err);
      }
      else {
        console.error('init() err =', err);
      }
      throw err;
    }

    this.mediaRecorder = new MediaRecorder(this.stream);

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      console.log('mediaRecorder.ondataavailable', event);

      const blob: Blob = event.data;
      // console.log('blob = ', blob);

      this.zone.run(() => {
        loadAudioFromBlob(blob).then((arrayBuffer: ArrayBuffer) => {
          if (this.recording$ === null) {
            throw new Error('RecorderService::init() loadAudioFromFile() recording$ is null');
          }
          this.recording$.next(arrayBuffer);
          this.recording$.complete();
        });
      });
    };

    this.mediaRecorder.onstop = (event: Event): any => {
      console.log('mediaRecorder.onstop', event);
    }

    this.stream.getTracks().forEach(track => {
      track.removeEventListener('ended', this.onTrackEnded);
      track.addEventListener('ended', this.onTrackEnded);
    });

  }

  public async start(): Promise<void> {
    console.warn('RecorderService::start()', this.mediaRecorder);

    await this.init();

    if (this.mediaRecorder === null) {
      throw new Error('mediaRecorder is null');
    }
    this.mediaRecorder.start();
    this.recording$ = new Subject<ArrayBuffer>();
  }

  public stop(): Observable<ArrayBuffer> {
    console.warn('RecorderService::stop()', this.mediaRecorder);
    if (this.mediaRecorder === null) {
      throw new Error('mediaRecorder is null');
    }
    if (this.recording$ === null) {
      throw new Error('recording$ is null');
    }
    this.mediaRecorder.stop();
    return this.recording$.asObservable()
      .pipe(
        finalize(() => {
          this.stream?.getTracks().forEach(track => track.stop());
          this.recording$ = null;
          this.stream = null;
          this.mediaRecorder = null;
        })
      );
  }

  // private async micPermission(){
  //   const result = await navigator.permissions.query({name: 'microphone'});
  //   return result;
  // }

  /**
   * 마이크가 존재하는지 확인한다.
   */
  public async hasRecorder(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        reject('MediaDevices API not available');
      }

      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const hasMic = devices.some(device => device.kind === 'audioinput');
          resolve(hasMic);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * 마이크 권한 확인 및 스트림 생성
   *
   * 이 함수는 navigator.mediaDevices.getUserMedia를 사용하여 마이크 권한을 확인하고 스트림을 생성합니다.
   * 사용자가 브라우저의 팝업창을 통해 마이크 권한을 허용하거나 차단할 때까지 기다립니다.
   * 권한이 허용된 경우, 함수는 MediaStream 객체를 반환합니다.
   * 권한이 거부된 경우, '마이크 사용 권한이 없습니다.' 에러를 발생시킵니다.
   * 기타 오류가 발생한 경우, '마이크를 사용할 수 없습니다: ${err.message}' 에러를 발생시킵니다.
   *
   * @param {MediaStreamConstraints} constraints - getUserMedia에 전달할 제약 조건
   * @returns {Promise<MediaStream>} 마이크 스트림
   * @throws {Error} 마이크 사용 권한이 없거나, 기타 마이크 사용에 오류가 발생한 경우
   */
  private async getMediaStream(constraints: MediaStreamConstraints): Promise< MediaStream> {
    if (navigator.mediaDevices.getUserMedia === undefined) {
      throw new Error('getUserMedia is not implemented in this browser');
    }
    return navigator.mediaDevices.getUserMedia(constraints);
  }

}
